import type { HoverActionKey } from 'owa-outlook-service-option-store';
import loc from 'owa-localize';
import { archive } from 'owa-locstrings/lib/strings/archive.locstring.json';
import { deleteItem } from 'owa-locstrings/lib/strings/deleteitem.locstring.json';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import { moveTo } from 'owa-locstrings/lib/strings/moveto.locstring.json';
import { unpin } from 'owa-locstrings/lib/strings/unpin.locstring.json';
import { default as Archive } from 'owa-fluent-icons-svg/lib/icons/ArchiveRegular';
import { default as Checkmark } from 'owa-fluent-icons-svg/lib/icons/CheckmarkRegular';
import { default as Delete } from 'owa-fluent-icons-svg/lib/icons/DeleteRegular';
import { default as FlagIconFilled } from 'owa-fluent-icons-svg/lib/icons/FlagFilled';
import { default as Flag } from 'owa-fluent-icons-svg/lib/icons/FlagRegular';
import { default as FolderArrowRight } from 'owa-fluent-icons-svg/lib/icons/FolderArrowRightRegular';
import { default as MailRead } from 'owa-fluent-icons-svg/lib/icons/MailReadRegular';
import { default as Mail } from 'owa-fluent-icons-svg/lib/icons/MailRegular';
import { default as PinIconFilled } from 'owa-fluent-icons-svg/lib/icons/PinFilled';
import { default as Pin } from 'owa-fluent-icons-svg/lib/icons/PinRegular';
import { default as Settings } from 'owa-fluent-icons-svg/lib/icons/SettingsRegular';
import {
    flagIconColor,
    flagFilledColor,
    completeIcon,
    flagButtonColor,
    flagButtonFilledColor,
} from 'owa-mail-listitem-styles/lib/scss/FlagIconStyles.scss';
import {
    markAsReadIconHoverColor,
    archiveIconHoverColor,
    deleteIconHoverColor,
    moveIconHoverColor,
    pinIconColor,
    pinFilledColor,
    pinButtonColor,
    pinButtonFilledColor,
    archiveButtonColor,
    markAsReadButtonColor,
    deleteButtonColor,
    moveButtonColor,
} from 'owa-mail-listitem-styles/lib/scss/MailListItemIconStyles.scss';
import { pinTitle, settingsTitle } from './getHoverActionIcon.locstring.json';
import { flagTitle, unflagTitle } from './getFlagCompleteHelper.locstring.json';
import classnames from 'owa-classnames';
import {
    ArchiveFilled,
    ArchiveRegular,
    bundleIcon,
    CheckmarkFilled,
    CheckmarkRegular,
    DeleteFilled,
    DeleteRegular,
    FolderArrowRightFilled,
    FolderArrowRightRegular,
    MailFilled,
    MailReadFilled,
    MailReadRegular,
    MailRegular,
    FlagFilled,
    FlagRegular,
    PinRegular,
    PinFilled,
    SettingsFilled,
    SettingsRegular,
} from '@fluentui/react-icons';
import { createIconModule } from 'owa-fluent-icons-svg';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { HoverActionIconProps } from '../types/IconProps';

const ArchiveBundleIcon = createIconModule(
    'ArchiveBundle',
    bundleIcon(ArchiveFilled, ArchiveRegular)
);
const FolderArrowRightBundleIcon = createIconModule(
    'FolderArrowRightBundle',
    bundleIcon(FolderArrowRightFilled, FolderArrowRightRegular)
);
const DeleteBundleIcon = createIconModule('DeleteBundle', bundleIcon(DeleteFilled, DeleteRegular));
const MailUnreadBundleIcon = createIconModule(
    'MailUnreadBundle',
    bundleIcon(MailReadFilled, MailReadRegular)
);
const MailReadBundleIcon = createIconModule('MailReadBundle', bundleIcon(MailFilled, MailRegular));
const CheckmarkBundleIcon = createIconModule(
    'CheckmarkBundle',
    bundleIcon(CheckmarkFilled, CheckmarkRegular)
);
const FlagBundleIcon = createIconModule('FlagBundle', bundleIcon(FlagFilled, FlagRegular));
const PinBundleIcon = createIconModule('PinBundle', bundleIcon(PinFilled, PinRegular));
const SettingsBundleIcon = createIconModule(
    'SettingsBundle',
    bundleIcon(SettingsFilled, SettingsRegular)
);

export default function getHoverActionIcon(
    action: HoverActionKey,
    isFlagged: boolean,
    isComplete: boolean,
    isPinned: boolean,
    unreadCount: number
): {
    title: string;
    key: string;
    iconName: string;
    iconClassName?: string;
} {
    const shouldShowIconBarV9Upgrade = isGlobalFeatureEnabled('tri-mlIconBarV9Upgrade');
    if (shouldShowIconBarV9Upgrade) {
        return getHoverActionIconNext(action, isFlagged, isComplete, isPinned, unreadCount);
    }
    switch (action) {
        case 'Archive':
            return {
                title: loc(archive),
                key: 'archiveIcon',
                iconName: ArchiveBundleIcon,
                iconClassName: classnames(archiveIconHoverColor),
            };
        case 'Move':
            return {
                title: loc(moveTo),
                key: 'moveIcon',
                iconName: FolderArrowRightBundleIcon,
                iconClassName: classnames(moveIconHoverColor),
            };
        case 'Delete':
            return {
                title: loc(deleteItem),
                key: 'deleteIcon',
                iconName: DeleteBundleIcon,
                iconClassName: classnames(deleteIconHoverColor),
            };
        case 'ReadUnread':
            const hasUnread = unreadCount && unreadCount > 0;
            const readUnreadBundle = hasUnread ? MailUnreadBundleIcon : MailReadBundleIcon;
            return {
                title: hasUnread ? loc(markAsRead) : loc(markAsUnread),
                key: 'markReadIcon',
                iconName: readUnreadBundle,
                iconClassName: classnames(markAsReadIconHoverColor),
            };
        case 'FlagUnflag':
            const flagIconName = isComplete
                ? CheckmarkBundleIcon
                : isFlagged
                ? FlagIconFilled
                : FlagBundleIcon;
            return {
                title: isComplete ? loc(flagTitle) : isFlagged ? loc(unflagTitle) : loc(flagTitle),
                key: isComplete ? 'completeIcon' : 'flagIcon',
                iconClassName: classnames(
                    isComplete ? completeIcon : isFlagged ? flagFilledColor : flagIconColor
                ),
                iconName: flagIconName,
            };
        case 'PinUnpin': {
            const pinClasses = classnames(isPinned ? pinFilledColor : pinIconColor);

            return {
                title: isPinned ? loc(unpin) : loc(pinTitle),
                key: 'pinIcon',
                iconClassName: classnames(pinClasses),
                iconName: isPinned ? PinIconFilled : PinBundleIcon,
            };
        }
        case 'Settings': {
            return {
                title: loc(settingsTitle),
                key: 'settingsIcon',
                iconName: SettingsBundleIcon,
            };
        }
        case 'None':
        default:
            return { title: '', key: '', iconName: '' };
    }
}

function getHoverActionIconNext(
    action: HoverActionKey,
    isFlagged: boolean,
    isComplete: boolean,
    isPinned: boolean,
    unreadCount: number
): HoverActionIconProps {
    switch (action) {
        case 'Archive':
            return {
                title: loc(archive),
                key: 'archiveIcon',
                id: action,
                iconName: Archive,
                buttonClasses: classnames(archiveButtonColor),
            };
        case 'Move':
            return {
                title: loc(moveTo),
                key: 'moveIcon',
                id: action,
                iconName: FolderArrowRight,
                buttonClasses: classnames(moveButtonColor),
            };
        case 'Delete':
            return {
                title: loc(deleteItem),
                key: 'deleteIcon',
                id: action,
                iconName: Delete,
                buttonClasses: classnames(deleteButtonColor),
            };
        case 'ReadUnread':
            const hasUnread = unreadCount && unreadCount > 0;
            return {
                title: hasUnread ? loc(markAsRead) : loc(markAsUnread),
                key: 'markReadIcon',
                id: action,
                iconName: hasUnread ? MailRead : Mail,
                buttonClasses: classnames(markAsReadButtonColor),
            };
        case 'FlagUnflag':
            const flagClass = classnames(
                isComplete ? completeIcon : isFlagged ? flagButtonFilledColor : flagButtonColor
            );
            return {
                title: isComplete ? loc(flagTitle) : isFlagged ? loc(unflagTitle) : loc(flagTitle),
                key: isComplete ? 'completeIcon' : 'flagIcon',
                id: action,
                iconName: isComplete ? Checkmark : isFlagged ? FlagIconFilled : Flag,
                buttonClasses: flagClass,
            };
        case 'PinUnpin': {
            const pinClasses = classnames(isPinned ? pinButtonFilledColor : pinButtonColor);

            return {
                title: isPinned ? loc(unpin) : loc(pinTitle),
                key: 'pinIcon',
                id: action,
                iconName: isPinned ? PinIconFilled : Pin,
                buttonClasses: pinClasses,
            };
        }
        case 'Settings': {
            return {
                title: loc(settingsTitle),
                key: 'settingsIcon',
                id: action,
                iconName: Settings,
                buttonClasses: classnames(moveButtonColor),
            };
        }
        case 'None':
        default:
            return { title: '', key: '', iconName: '' };
    }
}
